/**
 * Utility functions for file upload validation and verification.
 * Provides MD5 calculation, ETag validation, and OBS verification.
 */

import SparkMD5 from 'spark-md5';
import { verifyFile, deleteObject, notifyETagMismatch } from '@/api/storage';
import { uploadLog, uploadWarn, uploadError } from './uploadLogger';

/**
 * Calculate MD5 hash of a file using spark-md5 library.
 * Returns hex string (lowercase).
 *
 * @param file The file to calculate MD5 for
 * @returns Promise resolving to MD5 hash (hex string)
 */
export const calculateFileMD5 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const spark = new SparkMD5.ArrayBuffer();
    const reader = new FileReader();
    let currentChunk = 0;
    const chunkSize = 2097152; // 2MB chunk size
    const chunks = Math.ceil(file.size / chunkSize);

    reader.onload = (e) => {
      if (e.target?.result) {
        spark.append(e.target.result as ArrayBuffer);
        currentChunk++;

        if (currentChunk < chunks) {
          loadNext();
        } else {
          resolve(spark.end().toLowerCase());
        }
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    const loadNext = () => {
      const start = currentChunk * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      reader.readAsArrayBuffer(file.slice(start, end));
    };

    loadNext();
  });
};

/**
 * Validate file upload by checking:
 * 1. HTTP response status
 * 2. ETag from response headers
 * 3. File existence in OBS (optional backend verification)
 *
 * @param response The fetch response from OBS upload
 * @param expectedMD5 The expected MD5 hash (hex string)
 * @param objectKey The OBS object key for backend verification
 * @param fileName The file name for logging
 * @returns Promise resolving to validated etag
 * @throws Error if validation fails
 */
export const validateUploadResponse = async (
  response: Response,
  expectedMD5: string,
  objectKey: string,
  fileName: string
): Promise<string> => {
  // 1. Check HTTP status
  if (!response.ok) {
    throw new Error(
      `OBS upload failed: ${response.status} ${response.statusText}`
    );
  }

  // 2. Extract and validate ETag
  const etag = response.headers.get('ETag')?.replace(/"/g, '') || '';
  if (!etag) {
    throw new Error('No ETag returned from OBS upload');
  }

  const etagLower = etag.toLowerCase();
  const expectedLower = expectedMD5.toLowerCase();

  if (etagLower !== expectedLower) {
    // Log ETag mismatch for backend debugging
    await notifyETagMismatch({
      file_name: fileName,
      file_size: 0, // Will be updated by caller if needed
      mode: 'Direct',
      expected_md5: expectedMD5,
      received_etag: etag,
      timestamp: new Date().toISOString(),
      error_message: `ETag mismatch after direct upload (key: ${objectKey})`,
    }).catch(uploadWarn);

    throw new Error(
      `File integrity check failed: MD5 mismatch (expected: ${expectedMD5}, got: ${etag})`
    );
  }

  return etag;
};

/**
 * Verify file exists in OBS after upload completion.
 * Called after upload to ensure file was properly saved.
 *
 * @param objectKey The OBS object key to verify
 * @param expectedSize The expected file size (optional)
 * @param fileName The file name for logging
 * @throws Error if verification fails
 */
export const verifyUploadedFile = async (
  objectKey: string,
  expectedSize?: number,
  fileName?: string
): Promise<void> => {
  try {
    const result = await verifyFile(objectKey, expectedSize);

    if (!result.exists) {
      throw new Error(
        `File verification failed: file does not exist in OBS${
          result.error ? ` (${result.error})` : ''
        }`
      );
    }

    if (expectedSize && result.size !== expectedSize) {
      throw new Error(
        `File size mismatch: expected ${expectedSize}, got ${result.size}`
      );
    }

    uploadLog(`[Upload:Verified] File ${fileName} verified in OBS:`, result);
  } catch (error) {
    uploadError(`[Upload:VerificationFailed] ${fileName}:`, error);
    throw error;
  }
};

/**
 * Clean up OBS file after upload failure.
 * Attempts to delete the uploaded file to prevent orphaned objects.
 *
 * @param objectKey The OBS object key to delete
 * @param fileName The file name for logging
 */
export const cleanupFailedUpload = async (
  objectKey: string,
  fileName: string
): Promise<void> => {
  try {
    await deleteObject(objectKey);
    uploadLog(`[Upload:Cleanup] Deleted orphaned file from OBS: ${fileName}`);
  } catch (error) {
    uploadWarn(
      `[Upload:CleanupFailed] Failed to delete OBS file ${fileName}:`,
      error
    );
    // Don't throw - cleanup failure shouldn't break error handling
  }
};
